import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Trash2, ChevronDown, UtensilsCrossed } from "lucide-react";

const MEAL_LABEL = { breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner", snack: "Snack" };
const photoUrl = (path) => `${process.env.REACT_APP_BACKEND_URL}/api/community-recipes/photo/${path}?auth=${localStorage.getItem("spc_token") || sessionStorage.getItem("spc_token") || ""}`;

export default function RecipeSubmissions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(null);
  const [edits, setEdits] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/community-recipes"); setItems(data.recipes || []); }
    catch (e) { console.error("Admin: load community recipes failed", e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pending = items.filter((r) => r.status === "pending");
  const reviewed = items.filter((r) => r.status !== "pending");

  const setEdit = (id, k, v) => setEdits((s) => ({ ...s, [id]: { ...s[id], [k]: v } }));
  const val = (r, k) => (edits[r.id]?.[k] ?? (r[k] ?? ""));
  const tagsVal = (r) => (edits[r.id]?.tags ?? (r.tags || []).join(", "));

  const act = async (r, action) => {
    setBusy(r.id);
    try {
      if (action === "approve") {
        if (edits[r.id]) {
          await api.put(`/admin/community-recipes/${r.id}`, {
            kcal: val(r, "kcal"), protein: val(r, "protein"), carb: val(r, "carb"), fat: val(r, "fat"),
            tags: tagsVal(r).split(",").map((t) => t.trim()).filter(Boolean),
          });
        }
        await api.post(`/admin/community-recipes/${r.id}/approve`);
        toast.success("Approved — now live on Recipe Ideas");
      } else if (action === "reject") {
        await api.post(`/admin/community-recipes/${r.id}/reject`);
        toast("Rejected");
      } else if (action === "delete") {
        await api.delete(`/admin/community-recipes/${r.id}`);
        toast("Deleted");
      }
      await load();
    } catch (err) { toast.error("Action failed — please retry."); }
    finally { setBusy(null); }
  };

  const macroInput = "w-full bg-black border border-white/15 text-white text-sm px-2 py-1 rounded-none";

  return (
    <div className="mb-10 border border-[#FF9500]/30 p-5" data-testid="admin-recipe-submissions">
      <div className="flex items-center gap-2 mb-3">
        <UtensilsCrossed className="h-4 w-4 text-[#FF9500]" />
        <h3 className="font-display uppercase tracking-tight text-white">Recipe submissions</h3>
        <span className="text-[10px] font-bold uppercase tracking-wider text-black bg-[#FF9500] px-2 py-0.5" data-testid="recipe-pending-count">{pending.length} pending</span>
      </div>

      {loading ? (
        <p className="text-sm text-zinc-400">Loading…</p>
      ) : pending.length === 0 ? (
        <p className="text-sm text-zinc-400" data-testid="recipe-submissions-empty">No recipes waiting for review.</p>
      ) : (
        <div className="space-y-2">
          {pending.map((r) => {
            const open = openId === r.id;
            return (
              <div key={r.id} data-testid={`recipe-sub-${r.id}`} className="border border-white/10 bg-[#141414]">
                <button type="button" onClick={() => setOpenId(open ? null : r.id)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-white/[0.03]">
                  <span className="min-w-0">
                    <span className="text-sm font-semibold text-white block truncate">{r.name}</span>
                    <span className="text-[11px] text-zinc-400">{MEAL_LABEL[r.meal] || r.meal}{(r.tags || []).length ? " · " + r.tags.join(" · ") : ""}</span>
                  </span>
                  <ChevronDown className={`h-4 w-4 text-zinc-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                {open && (
                  <div className="px-3 pb-3 pt-2 border-t border-white/5 space-y-3">
                    {r.photo && <img src={photoUrl(r.photo)} alt="" className="w-full max-h-56 object-cover border border-white/10" />}
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#4C9AFF] mb-1">Ingredients</p>
                      <ul className="text-sm text-zinc-200 list-disc pl-5 space-y-0.5">{(r.ingredients || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#E2E30A] mb-1">Method</p>
                      <ol className="text-sm text-zinc-200 list-decimal pl-5 space-y-0.5">{(r.method || []).map((x, i) => <li key={i}>{x}</li>)}</ol>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1">Macros — add if missing before approving</p>
                      <div className="grid grid-cols-4 gap-2">
                        <input data-testid={`recipe-sub-kcal-${r.id}`} className={macroInput} type="number" placeholder="kcal" value={val(r, "kcal")} onChange={(e) => setEdit(r.id, "kcal", e.target.value)} />
                        <input className={macroInput} type="number" placeholder="P g" value={val(r, "protein")} onChange={(e) => setEdit(r.id, "protein", e.target.value)} />
                        <input className={macroInput} type="number" placeholder="C g" value={val(r, "carb")} onChange={(e) => setEdit(r.id, "carb", e.target.value)} />
                        <input className={macroInput} type="number" placeholder="F g" value={val(r, "fat")} onChange={(e) => setEdit(r.id, "fat", e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1">Tags (comma separated)</p>
                      <input className={macroInput} value={tagsVal(r)} onChange={(e) => setEdit(r.id, "tags", e.target.value)} placeholder="Vegan, High protein" />
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                      <button data-testid={`recipe-approve-${r.id}`} disabled={busy === r.id} onClick={() => act(r, "approve")}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-wider bg-[#34C759] text-black font-bold hover:bg-[#2fb350] disabled:opacity-50"><Check className="h-3.5 w-3.5" /> Approve</button>
                      <button data-testid={`recipe-reject-${r.id}`} disabled={busy === r.id} onClick={() => act(r, "reject")}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-wider border border-white/20 text-zinc-300 hover:text-white disabled:opacity-50"><X className="h-3.5 w-3.5" /> Reject</button>
                      <button data-testid={`recipe-delete-${r.id}`} disabled={busy === r.id} onClick={() => act(r, "delete")}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-wider border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 disabled:opacity-50"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {reviewed.length > 0 && (
        <p className="text-[11px] text-zinc-300 mt-3">
          {reviewed.filter((r) => r.status === "approved").length} approved · {reviewed.filter((r) => r.status === "rejected").length} rejected
        </p>
      )}
    </div>
  );
}
